import type { FooterRowProps } from '@components/Footer/FooterNavigationGroup'
import type { AppLocales } from '@constants/i18n'
import type { NavItems } from '@helpers/getFooter'
import { getFullUrlForFeature } from '@helpers/getFullUrlForFeature'
import { notEmpty } from '@helpers/notEmpty'
import { getDataFromReadMore } from '@helpers/getDataFromReadMore'
import type { AccordionGroupProps } from '@components/Accordion'
import type { HeaderLink } from '@cms/data/getHeaderData'

const formatItems = (
  navItems: NavItems['navigationItems'],
  locale: AppLocales
) => {
  let itemsHaveNew = false
  const navItemsData = navItems.reduce<HeaderLink[]>((items, item) => {
    const { link, text, pageLink, trackingEvent } = item
    let snowplowTrackingData

    if (trackingEvent) {
      snowplowTrackingData = {
        action: trackingEvent,
        // we do not have a label in clownfish so passing empty
        label: '',
      }
    }

    if (text && link) {
      if (item?.new) itemsHaveNew = true

      items.push({
        label: text,
        href: link,
        new: item?.new || false,
        textVariant: 'standard',
        snowplowTrackingData,
      })
    } else if (text && pageLink && pageLink.slug) {
      const { slug, type } = pageLink

      const fullUrl = getFullUrlForFeature(locale, slug, type)

      if (fullUrl) {
        if (item?.new) itemsHaveNew = true
        items.push({
          label: text,
          href: fullUrl,
          new: item?.new || false,
          textVariant: 'standard',
          snowplowTrackingData,
        })
      }
    }

    return items
  }, [])

  return {
    navItemsData,
    itemsHaveNew,
  }
}

type ColumnData = NonNullable<FooterRowProps['columnsData']>['firstColumn']

const formatColumn = (
  column: NonNullable<FooterRowProps['columnsData']>['firstColumn'],
  locale: AppLocales
) => {
  if (!column) {
    return {
      columnData: [],
    }
  }

  let footerAdData

  const columnData = column?.reduce<AccordionGroupProps[]>(
    (columnItems, columnItem) => {
      if (columnItem.type === 'navItem') {
        if (columnItem.name) {
          const linksData = formatItems(
            columnItem.navigationItems.filter(notEmpty),
            locale
          )
          const itemData = {
            label: columnItem.name,
            links: linksData.navItemsData,
            new: linksData.itemsHaveNew,
          }
          const readMoreData = getDataFromReadMore(locale, columnItem.readMore)

          if (readMoreData) {
            itemData.links.push({
              ...readMoreData,
              textColor: 'teal',
              textVariant: 'standard-bold',
            })
          }

          columnItems.push(itemData)
        }
      }

      if (columnItem.type === 'navCluster' && columnItem.navigationCluster) {
        columnItem.navigationCluster.forEach(clusterItem => {
          if (clusterItem.name) {
            const { navItemsData, itemsHaveNew } = formatItems(
              clusterItem.navigationItems.filter(notEmpty),
              locale
            )
            const itemData = {
              label: clusterItem.name,
              links: navItemsData,
              new: itemsHaveNew,
            }
            const readMoreData = getDataFromReadMore(
              locale,
              clusterItem.readMore
            )

            if (readMoreData) {
              itemData.links.push({
                ...readMoreData,
                textColor: 'teal',
                textVariant: 'standard-bold',
              })
            }

            columnItems.push(itemData)
          }
        })
      }

      if (columnItem.type === 'FooterAd') {
        footerAdData = columnItem
      }

      return columnItems
    },
    []
  )

  return {
    columnData,
    footerAdData,
  }
}

export const getMobileMenuData = (
  rowData: FooterRowProps['columnsData'],
  locale?: AppLocales
) => {
  if (!locale || !rowData) return null

  const { firstColumn, secondColumn } = rowData
  const columnData: ColumnData = []
  if (firstColumn) columnData.push(...firstColumn)
  if (secondColumn) columnData.push(...secondColumn)

  return formatColumn(columnData, locale)
}
